import React, { useEffect, useState } from "react";
import { Page, PageHeader } from "../components/PageHeader";
import { api, extractError } from "../lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";

const EMPTY = { codigo: "", nombre: "", descripcion: "", activo: true };

export default function CentrosCosto() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = async () => {
    try {
      const { data } = await api.get("/centros-costo");
      setList(data);
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const reset = () => { setEditing(null); setForm(EMPTY); };

  const openEdit = (c) => {
    setEditing(c);
    setForm({ codigo: c.codigo, nombre: c.nombre, descripcion: c.descripcion || "", activo: c.activo !== false });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/centros-costo/${editing.id}`, form);
        toast.success("Centro de costo actualizado");
      } else {
        await api.post("/centros-costo", form);
        toast.success("Centro de costo creado");
      }
      setOpen(false);
      reset();
      load();
    } catch (err) {
      toast.error(extractError(err));
    }
  };

  const del = async (id) => {
    if (!window.confirm("¿Eliminar este centro de costo?")) return;
    try {
      await api.delete(`/centros-costo/${id}`);
      toast.success("Eliminado");
      load();
    } catch (e) {
      toast.error(extractError(e));
    }
  };

  return (
    <Page>
      <PageHeader
        title="Centros de costo"
        subtitle="Dimensiones analíticas para asignar en líneas de comprobantes"
        testid="centros-costo-header"
        actions={
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
            <DialogTrigger asChild>
              <button className="inline-flex items-center gap-1.5 bg-primary hover:bg-success text-primary-foreground text-sm font-medium px-3 py-2 rounded-sm">
                <Plus className="h-4 w-4" /> Nuevo centro
              </button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editing ? "Editar centro de costo" : "Nuevo centro de costo"}</DialogTitle>
              </DialogHeader>
              <form onSubmit={submit} className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="label-caps block mb-1">Código</label>
                    <input required value={form.codigo} onChange={(e) => setForm({ ...form, codigo: e.target.value })}
                      className="w-full px-3 py-2 border border-border rounded-sm text-sm font-mono" />
                  </div>
                  <div>
                    <label className="label-caps block mb-1">Nombre</label>
                    <input required value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                      className="w-full px-3 py-2 border border-border rounded-sm text-sm" />
                  </div>
                </div>
                <div>
                  <label className="label-caps block mb-1">Descripción</label>
                  <input value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
                    className="w-full px-3 py-2 border border-border rounded-sm text-sm" />
                </div>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={form.activo} onChange={(e) => setForm({ ...form, activo: e.target.checked })} />
                  Activo
                </label>
                <DialogFooter>
                  <button type="submit" className="bg-primary hover:bg-success text-primary-foreground text-sm px-4 py-2 rounded-sm">
                    {editing ? "Guardar" : "Crear"}
                  </button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="bg-card border border-border rounded-md overflow-x-auto">
        {loading ? (
          <div className="p-8 text-center text-sm text-muted-foreground">Cargando…</div>
        ) : list.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">Sin centros de costo. Crea el primero.</div>
        ) : (
          <table className="acc-table">
            <thead>
              <tr><th>Código</th><th>Nombre</th><th>Descripción</th><th>Estado</th><th style={{ width: 80 }}></th></tr>
            </thead>
            <tbody>
              {list.map((c) => (
                <tr key={c.id}>
                  <td className="font-mono text-sm">{c.codigo}</td>
                  <td>{c.nombre}</td>
                  <td className="text-muted-foreground text-sm">{c.descripcion || "—"}</td>
                  <td>{c.activo !== false ? "Activo" : "Inactivo"}</td>
                  <td>
                    <div className="flex gap-1">
                      <button onClick={() => openEdit(c)} className="text-primary hover:bg-primary/10 p-1 rounded-sm"><Pencil className="h-3.5 w-3.5" /></button>
                      <button onClick={() => del(c.id)} className="text-destructive hover:bg-destructive/10 p-1 rounded-sm"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </Page>
  );
}
